/**
 * NeighborMan.jsx — Web version of Blessed's Neighbor-Man mini-game.
 * Ported from the mobile React Native version to plain React DOM.
 * Wired to Blessed Web's api proxy + auth (no raw fetch, no CORS).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { userCoords } from "@/hooks/useGeo";
import { normRecipient, UNLIMITED_RADIUS } from "@/lib/blessed";
import { AmountModal } from "@/pages/GamePlay";

/* -------- Maze (0=dot, 1=wall, 2=empty walkable no dot) -------- */
// prettier-ignore
const MAZE = [
  [1,1,1,1,1,1,1,1,1,1,1,1,1],
  [1,0,0,0,0,0,1,0,0,0,0,0,1],
  [1,0,1,1,0,0,0,0,0,1,1,0,1],
  [1,0,0,0,0,1,1,1,0,0,0,0,1],
  [1,1,1,0,0,1,2,1,0,0,1,1,1],
  [2,2,1,0,0,0,2,0,0,0,1,2,2],
  [1,1,1,0,1,2,2,2,1,0,1,1,1],
  [1,0,0,0,1,2,2,2,1,0,0,0,1],
  [1,0,1,0,0,0,0,0,0,0,1,0,1],
  [1,0,1,1,0,1,1,1,0,1,1,0,1],
  [1,0,0,0,0,0,0,0,0,0,0,0,1],
  [1,1,0,1,1,1,0,1,1,1,0,1,1],
  [1,0,0,0,0,0,0,0,0,0,0,0,1],
  [1,0,1,1,1,1,1,1,1,1,1,0,1],
  [1,1,1,1,1,1,1,1,1,1,1,1,1],
];
const ROWS = MAZE.length;
const COLS = MAZE[0].length;
const GHOST_START = [[6, 5], [6, 6], [6, 7], [7, 6]];
const PACMAN_START = [12, 6];
const DIRS = {
  up: [-1, 0],
  down: [1, 0],
  left: [0, -1],
  right: [0, 1],
  stop: [0, 0],
};
const GHOST_COLORS = ["#F04B4B", "#FFB8FF", "#00E7E7", "#FFB852"];
function pacIntervalFor(level)   { return Math.max(70, 140 - (level - 1) * 8); }
function ghostIntervalFor(level) { return Math.max(80, 160 - (level - 1) * 10); }
function ghostSmartnessFor(level){ return Math.min(0.98, 0.2 + (level - 1) * 0.09); }
function inBounds(r, col) { return r >= 0 && r < ROWS && col >= 0 && col < COLS; }
function walkable(r, col) {
  if (!inBounds(r, col)) return false;
  return MAZE[r][col] !== 1;
}
function makeDotGrid() { return MAZE.map((row) => row.map((cell) => cell === 0)); }

/* -------- Component (page) -------- */
export default function NeighborMan() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const coords = useMemo(() => userCoords(user), [user]);
  const onBack = useCallback(() => navigate("/games"), [navigate]);
  const onDonate = useCallback(
    ({ recipientId, amount }) =>
      navigate(`/donate/${recipientId}?amount=${amount}&message=${encodeURIComponent("You caught me in Pac-Man! 👾")}`),
    [navigate],
  );

  const [pool, setPool] = useState([]);
  const [loadingPool, setLoadingPool] = useState(true);
  const [amountOpen, setAmountOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [level, setLevel] = useState(1);
  const [score, setScore] = useState(0);
  const [running, setRunning] = useState(false);
  const [ready, setReady] = useState(false);
  const [dots, setDots] = useState(() => makeDotGrid());
  const [pac, setPac] = useState(PACMAN_START);
  const [ghosts, setGhosts] = useState(GHOST_START);
  const [outcome, setOutcome] = useState(null); // { kind: 'won'|'lost', ghost }
  const dirRef = useRef("stop");
  const queuedRef = useRef("stop");
  const pacRef = useRef(PACMAN_START);
  useEffect(() => { pacRef.current = pac; }, [pac]);

  /* Fetch pool of neighbors — via Blessed Web api proxy (auth auto-attached) */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const params = { limit: 20, radius_miles: UNLIMITED_RADIUS };
        if (coords) { params.lat = coords.lat; params.lng = coords.lng; }
        const { data } = await api.get("/recipients/nearby", { params });
        const list = (Array.isArray(data) ? data : data.recipients || data.items || [])
          .map(normRecipient)
          .filter((x) => x.id && !x.isMe);
        if (!alive) return;
        const seed = list.slice(0, 20).map((r, i) => ({
          id: r.id,
          name: r.name || "Neighbor",
          picture: r.picture || null,
          color: GHOST_COLORS[i % GHOST_COLORS.length],
        }));
        const padded = [];
        if (seed.length === 0) {
          setPool([]);
        } else {
          for (let i = 0; i < 4; i++) padded.push({ ...seed[i % seed.length] });
          padded.forEach((n, i) => (n.color = GHOST_COLORS[i]));
          setPool(padded);
        }
      } catch {
        setPool([]);
      } finally {
        if (alive) setLoadingPool(false);
      }
    })();
    return () => { alive = false; };
  }, [coords]);

  useEffect(() => {
    if (!loadingPool && pool.length > 0 && !outcome && !running) setReady(true);
  }, [loadingPool, pool.length, outcome, running]);
  /* Pac-Man tick */
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => {
      setPac(([r, col]) => {
        const attempt = (d) => {
          const [dr, dc] = DIRS[d];
          const nr = r + dr;
          const nc = col + dc;
          if (walkable(nr, nc)) return [nr, nc];
          return null;
        };
        const q = queuedRef.current;
        if (q !== "stop") {
          const p = attempt(q);
          if (p) { dirRef.current = q; queuedRef.current = "stop"; return p; }
        }
        const p = attempt(dirRef.current);
        if (p) return p;
        return [r, col];
      });
    }, pacIntervalFor(level));
    return () => clearInterval(t);
  }, [running, level]);
  /* Ghost tick */
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => {
      const [tr, tc] = pacRef.current;
      setGhosts((gs) =>
        gs.map(([gr, gc]) => {
          const options = ["up", "down", "left", "right"].filter((d) => {
            const [dr, dc] = DIRS[d];
            return walkable(gr + dr, gc + dc);
          });
          if (options.length === 0) return [gr, gc];
          const smart = Math.random() < ghostSmartnessFor(level);
          if (smart) {
            let best = options[0];
            let bestD = Infinity;
            for (const d of options) {
              const [dr, dc] = DIRS[d];
              const dist = Math.abs(gr + dr - tr) + Math.abs(gc + dc - tc);
              if (dist < bestD) { bestD = dist; best = d; }
            }
            const [dr, dc] = DIRS[best];
            return [gr + dr, gc + dc];
          }
          const d = options[Math.floor(Math.random() * options.length)];
          const [dr, dc] = DIRS[d];
          return [gr + dr, gc + dc];
        }),
      );
    }, ghostIntervalFor(level));
    return () => clearInterval(t);
  }, [running, level]);
  /* Dot-eating & collisions */
  useEffect(() => {
    if (!running) return;
    setDots((prev) => {
      if (prev[pac[0]][pac[1]]) {
        const next = prev.map((row) => row.slice());
        next[pac[0]][pac[1]] = false;
        setScore((s) => s + 1);
        return next;
      }
      return prev;
    });
    const hitIdx = ghosts.findIndex(([gr, gc]) => gr === pac[0] && gc === pac[1]);
    if (hitIdx >= 0) {
      setRunning(false);
      setReady(false);
      setOutcome({ kind: "lost", ghost: pool[hitIdx] });
    }
  }, [pac, ghosts, running, pool]);
  /* Level complete? */
  useEffect(() => {
    if (!running) return;
    const remaining = dots.some((row) => row.some(Boolean));
    if (!remaining) {
      setRunning(false);
      if (level >= 10) {
        setReady(false);
        setOutcome({ kind: "won" });
      } else {
        setTimeout(() => {
          setLevel((l) => l + 1);
          setDots(makeDotGrid());
          setPac(PACMAN_START);
          setGhosts(GHOST_START);
          dirRef.current = "stop";
          queuedRef.current = "stop";
          setRunning(true);
        }, 800);
      }
    }
  }, [dots, running, level]);
  const startGame = useCallback(() => {
    setLevel(1);
    setScore(0);
    setDots(makeDotGrid());
    setPac(PACMAN_START);
    setGhosts(GHOST_START);
    dirRef.current = "stop";
    queuedRef.current = "stop";
    setOutcome(null);
    setReady(false);
    setRunning(true);
  }, []);
  useEffect(() => {
    if (outcome) {
      let t = outcome.ghost;
      if (outcome.kind === "won" && pool.length > 0) t = pool[Math.floor(Math.random() * pool.length)];
      setTarget(t || null);
      const id = setTimeout(() => {
        setAmountOpen(true);
        // Once the popup is up, send everyone back to their starting spots.
        setPac(PACMAN_START);
        setGhosts(GHOST_START);
        dirRef.current = "stop";
        queuedRef.current = "stop";
      }, 500);
      return () => clearTimeout(id);
    }
  }, [outcome, pool]);
  const press = useCallback((d) => {
    queuedRef.current = d;
    if (dirRef.current === "stop") dirRef.current = d;
  }, []);
  /* Keyboard controls */
  useEffect(() => {
    const handler = (e) => {
      const key = e.key.toLowerCase();
      if (["arrowup", "w"].includes(key)) press("up");
      else if (["arrowdown", "s"].includes(key)) press("down");
      else if (["arrowleft", "a"].includes(key)) press("left");
      else if (["arrowright", "d"].includes(key)) press("right");
      else if (key === "enter" || key === " ") {
        if (ready && !running) startGame();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [press, ready, running, startGame]);
  const closeModal = useCallback(() => {
    setAmountOpen(false);
    setOutcome(null);
    setTarget(null);
    setReady(true);
  }, []);
  const handlePick = useCallback((amt) => {
    setAmountOpen(false);
    if (!target) { onBack(); return; }
    onDonate({ recipientId: target.id, amount: amt });
  }, [target, onBack, onDonate]);
  const cellSize = 26;
  const boardW = COLS * cellSize;
  const boardH = ROWS * cellSize;
  /* Board swipe (pointer events) */
  const boardRef = useRef(null);
  const swipeRef = useRef({ x: 0, y: 0, active: false, lastDir: "stop" });
  const onBoardPointerDown = (e) => {
    swipeRef.current = { x: e.clientX, y: e.clientY, active: true, lastDir: "stop" };
  };
  const onBoardPointerMove = (e) => {
    const s = swipeRef.current;
    if (!s.active) return;
    const dx = e.clientX - s.x;
    const dy = e.clientY - s.y;
    const ax = Math.abs(dx);
    const ay = Math.abs(dy);
    if (ax < 12 && ay < 12) return;
    const d = ax > ay ? (dx > 0 ? "right" : "left") : dy > 0 ? "down" : "up";
    if (s.lastDir !== d) { s.lastDir = d; press(d); }
  };
  const onBoardPointerUp = () => { swipeRef.current.active = false; };
  /* Merged wall rectangles (row runs) */
  const wallRects = useMemo(() => {
    const out = [];
    for (let r = 0; r < ROWS; r++) {
      let start = null;
      for (let col = 0; col <= COLS; col++) {
        const isWall = col < COLS && MAZE[r][col] === 1;
        if (isWall && start === null) start = col;
        if (!isWall && start !== null) {
          out.push({
            key: `wr-${r}-${start}`,
            x: start * cellSize,
            y: r * cellSize,
            w: (col - start) * cellSize,
            h: cellSize,
          });
          start = null;
        }
      }
    }
    return out;
  }, [cellSize]);
  const pacDuration = pacIntervalFor(level);
  const ghostDuration = ghostIntervalFor(level);
  return (
    <div style={styles.container} data-testid="neighborman-game">
      <style>{sheet}</style>
      <div style={styles.header}>
        <button style={styles.iconBtn} onClick={onBack} aria-label="Back" data-testid="neighborman-back">←</button>
        <div style={styles.title}>Pac-Man</div>
        <div style={styles.levelPill}>Lvl {level}/10</div>
      </div>
      <div style={styles.scoreRow}>
        <div style={styles.scorePill}>
          <span style={styles.scoreDot} />
          <span style={styles.scoreText}>SCORE</span>
          <span style={styles.scoreNum} data-testid="neighborman-score">{score}</span>
        </div>
      </div>
      {loadingPool ? (
        <div style={styles.centerFill}>
          <div className="spinner" />
          <div style={styles.dim}>Finding neighbors nearby…</div>
        </div>
      ) : pool.length === 0 ? (
        <div style={styles.centerFill}>
          <div style={styles.dim}>
            No neighbors found in your radius. Widen your map radius and come back.
          </div>
          <button style={styles.primaryBtn} onClick={onBack}>Back to Games</button>
        </div>
      ) : (
        <>
          <div style={styles.stage}>
            <div
              ref={boardRef}
              style={{ ...styles.board, width: boardW, height: boardH }}
              onPointerDown={onBoardPointerDown}
              onPointerMove={onBoardPointerMove}
              onPointerUp={onBoardPointerUp}
              onPointerCancel={onBoardPointerUp}
            >
              {wallRects.map((w) => (
                <div key={w.key} style={{
                  position: "absolute",
                  left: w.x, top: w.y, width: w.w, height: w.h,
                  background: "#1B1BC7",
                  borderTop: "1px solid #4C6BFF",
                  borderBottom: "1px solid #4C6BFF",
                }} />
              ))}
              {dots.map((row, r) =>
                row.map((hasDot, col) => hasDot ? (
                  <div key={`d-${r}-${col}`} style={{
                    position: "absolute",
                    left: col * cellSize + cellSize / 2 - 2,
                    top: r * cellSize + cellSize / 2 - 2,
                    width: 4, height: 4,
                    background: "#FFD966",
                    borderRadius: 2,
                  }} />
                ) : null)
              )}
              <Actor
                gridR={pac[0]} gridC={pac[1]}
                cellSize={cellSize} durationMs={pacDuration}
                borderColor="#FFEC5C" bgColor="#FFEC5C"
                picture={user?.picture}
              />
              {ghosts.map(([gr, gc], i) => {
                const n = pool[i];
                return (
                  <Actor
                    key={`g-${i}`}
                    gridR={gr} gridC={gc}
                    cellSize={cellSize} durationMs={ghostDuration}
                    borderColor={n?.color || "#F04B4B"}
                    bgColor={n?.color || "#F04B4B"}
                    picture={n?.picture}
                    initial={n?.name?.[0]?.toUpperCase()}
                  />
                );
              })}
              {ready && !running && !outcome ? (
                <div style={styles.startOverlay}>
                  <button style={styles.startBtn} onClick={startGame} data-testid="neighborman-start">▶ START</button>
                  <div style={styles.startHint}>
                    Eat every dot · dodge {pool.length || 4} ghosts
                  </div>
                </div>
              ) : null}
            </div>
          </div>
          <GameBoyDpad onPress={press} />
        </>
      )}
      <AmountModal open={amountOpen && pool.length > 0} recipient={target} onClose={closeModal} onPick={handlePick} />
    </div>
  );
}
/* -------- Actor (glides between grid cells via CSS transition) -------- */
function Actor({ gridR, gridC, cellSize, durationMs, borderColor, bgColor, picture, initial }) {
  const x = gridC * cellSize + 1;
  const y = gridR * cellSize + 1;
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);
  return (
    <div style={{
      position: "absolute",
      left: 0, top: 0,
      width: cellSize - 2, height: cellSize - 2,
      borderRadius: (cellSize - 2) / 2,
      border: `2px solid ${borderColor}`,
      background: bgColor,
      overflow: "hidden",
      display: "flex",
      alignItems: "center", justifyContent: "center",
      color: "#fff",
      fontWeight: 900, fontSize: 11,
      transform: `translate(${x}px, ${y}px)`,
      transition: mounted ? `transform ${durationMs}ms linear` : "none",
      willChange: "transform",
    }}>
      {picture ? (
        <img src={picture} alt="" draggable={false} referrerPolicy="no-referrer"
          style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (initial || "")}
    </div>
  );
}
/* -------- Game Boy D-pad (pointer/drag joystick) -------- */
function GameBoyDpad({ onPress }) {
  const ARM = 56;
  const CENTER = 56;
  const size = ARM * 2 + CENTER;
  const lastDirRef = useRef("stop");
  const activeRef = useRef(false);
  const startRef = useRef({ x: 0, y: 0 });
  const emit = (d) => {
    if (lastDirRef.current !== d) {
      lastDirRef.current = d;
      onPress(d);
    }
  };
  const dirFromDelta = (dx, dy) => {
    const ax = Math.abs(dx);
    const ay = Math.abs(dy);
    if (ax < 4 && ay < 4) return lastDirRef.current;
    if (ax > ay) return dx > 0 ? "right" : "left";
    return dy > 0 ? "down" : "up";
  };
  const onDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    activeRef.current = true;
    const rect = e.currentTarget.getBoundingClientRect();
    startRef.current = { x: e.clientX, y: e.clientY };
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    emit(dirFromDelta(e.clientX - cx, e.clientY - cy));
  };
  const onMove = (e) => {
    if (!activeRef.current) return;
    const dx = e.clientX - startRef.current.x;
    const dy = e.clientY - startRef.current.y;
    emit(dirFromDelta(dx, dy));
  };
  const onUp = () => { activeRef.current = false; };
  return (
    <div style={{ display: "flex", justifyContent: "center", padding: "16px 0" }}>
      <div
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
        style={{
          position: "relative",
          width: size, height: size,
          touchAction: "none",
          userSelect: "none",
        }}
      >
        <div style={{
          position: "absolute", left: 0, top: ARM, width: size, height: CENTER,
          background: "#E8E8E8", border: "2px solid #111", borderRadius: 12,
          boxShadow: "0 3px 4px rgba(0,0,0,0.35)",
        }} />
        <div style={{
          position: "absolute", top: 0, left: ARM, width: CENTER, height: size,
          background: "#E8E8E8", border: "2px solid #111", borderRadius: 12,
          boxShadow: "0 3px 4px rgba(0,0,0,0.35)",
        }} />
        <div style={{
          position: "absolute",
          left: ARM + CENTER / 2 - 10, top: ARM + CENTER / 2 - 10,
          width: 20, height: 20, borderRadius: 10,
          background: "#B8B8B8", border: "1px solid rgba(0,0,0,0.25)",
        }} />
        <div style={{ position: "absolute", top: 8, left: ARM, width: CENTER, textAlign: "center", color: "#111", fontWeight: 900, pointerEvents: "none" }}>▲</div>
        <div style={{ position: "absolute", bottom: 8, left: ARM, width: CENTER, textAlign: "center", color: "#111", fontWeight: 900, pointerEvents: "none" }}>▼</div>
        <div style={{ position: "absolute", left: 8, top: ARM, height: CENTER, lineHeight: `${CENTER}px`, color: "#111", fontWeight: 900, pointerEvents: "none" }}>◀</div>
        <div style={{ position: "absolute", right: 8, top: ARM, height: CENTER, lineHeight: `${CENTER}px`, color: "#111", fontWeight: 900, pointerEvents: "none" }}>▶</div>
      </div>
    </div>
  );
}
/* -------- Styles -------- */
const styles = {
  container: {
    minHeight: "100vh",
    background: "#0B0B14",
    color: "#fff",
    fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    display: "flex",
    flexDirection: "column",
    paddingBottom: "calc(env(safe-area-inset-bottom) + 80px)",
  },
  header: { display: "flex", alignItems: "center", gap: 8, padding: "12px 16px" },
  iconBtn: {
    width: 40, height: 40, borderRadius: 20,
    background: "#1A1A2A", border: "none", color: "#fff",
    fontSize: 18, cursor: "pointer",
  },
  title: { flex: 1, textAlign: "center", fontSize: 20, fontWeight: 900 },
  levelPill: {
    background: "#4C6BFF", padding: "6px 10px", borderRadius: 999,
    fontWeight: 900, fontSize: 12,
  },
  scoreRow: { display: "flex", justifyContent: "center", paddingBottom: 8 },
  scorePill: {
    display: "inline-flex", alignItems: "center", gap: 8,
    background: "#1A1A2A", padding: "6px 14px", borderRadius: 999,
    border: "1px solid rgba(255,217,102,0.35)",
  },
  scoreDot: { width: 8, height: 8, borderRadius: 4, background: "#FFD966", display: "inline-block" },
  scoreText: { color: "#b8b8c8", fontWeight: 800, fontSize: 12, letterSpacing: 0.5 },
  scoreNum: { color: "#fff", fontWeight: 900, fontSize: 14, minWidth: 24, textAlign: "right" },
  stage: {
    flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
    background: "#000", margin: "0 16px", borderRadius: 12, padding: "8px 0",
  },
  board: { background: "#000", position: "relative", touchAction: "none" },
  startOverlay: {
    position: "absolute", inset: 0,
    display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center",
    background: "rgba(0,0,0,0.55)", gap: 10,
  },
  startBtn: {
    background: "#FFD966", color: "#0B0B0B", border: "none",
    padding: "14px 26px", borderRadius: 999,
    fontWeight: 900, fontSize: 18, letterSpacing: 2,
    cursor: "pointer", boxShadow: "0 3px 6px rgba(0,0,0,0.4)",
  },
  startHint: { color: "#fff", fontWeight: 700, fontSize: 12, textAlign: "center", padding: "0 20px" },
  centerFill: {
    flex: 1, display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center",
    padding: 24, gap: 12,
  },
  dim: { color: "#8b8ba0", fontWeight: 700, textAlign: "center", fontSize: 13 },
  primaryBtn: {
    background: "#4C6BFF", color: "#fff", border: "none",
    padding: "14px 24px", borderRadius: 999,
    fontWeight: 900, fontSize: 15, cursor: "pointer",
  },
  linkBtn: {
    background: "transparent", border: "none",
    color: "#8b8ba0", fontWeight: 700, padding: 8, cursor: "pointer",
  },
  backdrop: {
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,0.7)",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: 20, zIndex: 50,
  },
  modalCard: {
    width: "100%", maxWidth: 400,
    background: "#111120", borderRadius: 16, padding: 20,
    display: "flex", flexDirection: "column",
    alignItems: "center", gap: 12,
  },
  amountRow: {
    display: "flex", alignItems: "center", gap: 6,
    background: "#0B0B14", border: "1px solid #2a2a3a",
    borderRadius: 12, padding: "10px 14px", width: "100%",
  },
  amountPrefix: { color: "#8b8ba0", fontSize: 20, fontWeight: 900 },
  amountInput: {
    flex: 1, background: "transparent", border: "none",
    color: "#fff", fontSize: 24, fontWeight: 900, outline: "none",
  },
  chip: {
    background: "#1A1A2A", border: "1px solid #2a2a3a",
    color: "#fff", padding: "8px 14px", borderRadius: 999,
    fontWeight: 800, cursor: "pointer",
  },
};
const sheet = `
  .spinner {
    width: 32px; height: 32px;
    border: 3px solid rgba(255,255,255,0.15);
    border-top-color: #4C6BFF;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }
`;
